// Which drum already on a machine is THE SAME THING as the one being planned.
//
// A merge execution puts several products' shares on one bobbin. The picker
// (`ShareOntoDrumPicker`) lists every queued drum of the phase and ranks them:
// the ones a share could legally land on first, the rest behind a "show others"
// toggle — nothing is hidden, because a phase with nothing queued and a colour
// with nothing queued look identical from the outside.
//
// ── and what "the same thing" means depends on the phase ──────────────────
//
// The app already answers this in one place: `redis_dev/helpers/mergeSlot.js`
// `buildMergedOutputKey`, the key the LAUNCH uses to decide that two executions
// are one drum. This mirrors it, so what the picker offers and what the launch
// then does cannot disagree:
//
//   conductor_isolation  COLOUR, and the wire Ø where both are known. The metres
//                        are the same insulated conductor — which is why a
//                        CYYF 5x4 can take some of a 3x4's Ø2.14 blue.
//   rope                 MACHINE + drum material + drum Ø. A rope carries no
//                        colour and no wire at all.
//   anything else        as isolation.
//
// Rope was the gap. Judged by the isolation rule its `color` and `wireDiameter`
// are both null, so EVERY rope drum in the factory matched and the ranking said
// nothing — a CYYF 5x10's drum on another machine looked as good as the one the
// planner meant. Reported 2026-11-02 on the DTB1250, where a second CYYF 3x1.5
// had to join a bobbin already turning.
//
// ── only what is KNOWN narrows it ─────────────────────────────────────────
//
// Every comparison here is skipped when either side is absent. A planner who has
// not chosen the reel yet must still see the candidates: an empty row reads as
// "there is nothing to merge onto", which is the one thing it must not say when
// there is.
//
// Pure, and mirrored into tests/.generated by tests/tools/mirror.mjs.

const text = v => String(v ?? '').trim().toLowerCase();
const upper = v => String(v ?? '').trim().toUpperCase();

/** A number that was actually given — `Number(null)` is 0 and 0 is a real Ø. */
const given = v => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Two stated values that disagree. Either one absent is NOT a disagreement. */
const differs = (a, b) => {
  const x = text(a);
  const y = text(b);
  return !!x && !!y && x !== y;
};

const differsBy = (a, b, tolerance) => {
  const x = given(a);
  const y = given(b);
  return x != null && y != null && Math.abs(x - y) > tolerance;
};

/**
 * Could `drum` take a share of the execution described by `want`?
 *
 * @param drum  a row from /jobs/shareable-drums
 * @param want  { phase, color, wireDiameter, drumMaterial, drumDiameter, machineId }
 */
export function sameShareTarget(drum, want = {}) {
  if (!drum) return false;
  const phase = text(want.phase);

  if (phase === 'rope') {
    // The reel and the line — `buildMergedOutputKey`'s rope key.
    //
    // Ø within half a millimetre: a drum Ø is a nominal size typed by hand
    // (1250, 1400) and never a measurement, so an exact test would only ever
    // fail on a stray decimal.
    if (differs(want.drumMaterial, drum.drum_material)) return false;
    if (differsBy(want.drumDiameter, drum.drum_diameter, 0.5)) return false;
    if (differs(want.machineId, drum.machineId)) return false;
    return true;
  }

  // The conductor: same colour, same wire.
  const c = upper(want.color);
  const dc = upper(drum.color);
  if (c && dc !== c) return false;
  // 0.0005 mm — wire Ø IS measured, to three decimals (Ø2.14, Ø1.35).
  if (differsBy(want.wireDiameter, drum.wireDiameter, 0.0005)) return false;
  return true;
}

/**
 * The offered drums, ranked: the ones a share can land on, then the rest.
 *
 * Returned as two lists rather than one sorted one, because the view has to be
 * able to say "none of these is the same conductor" — which is a different
 * message from "nothing is queued".
 */
export function rankShareTargets(drums, want = {}) {
  const list = Array.isArray(drums) ? drums : [];
  const matching = [];
  const others = [];
  for (const d of list) (sameShareTarget(d, want) ? matching : others).push(d);
  return { matching, others };
}
